import { useState, useRef, useEffect } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight, Star, MoreHorizontal, Pencil, Trash2, X } from 'lucide-react';
import useBoardStore from '../../../store/boardStore';
import DraggableBoardItem from './DraggableBoardItem';
import { dialog } from '../../../store/dialogStore';

export default function FavoriteFolder({ folder, activeBoardId, onNavigate }) {
  const [expanded, setExpanded] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(folder.name);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const { renameFavoriteFolder, deleteFavoriteFolder, removeBoardFromFolder } = useBoardStore();

  const { setNodeRef, isOver } = useDroppable({
    id: `folder-${folder.id}`,
    data: { type: 'folder', folderId: folder.id },
  });

  useEffect(() => { setName(folder.name); }, [folder.name]);
  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const saveName = () => {
    setRenaming(false);
    const trimmed = name.trim();
    if (trimmed && trimmed !== folder.name) renameFavoriteFolder(folder.id, trimmed);
    else setName(folder.name);
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    if (await dialog.confirm(`Excluir pasta "${folder.name}"`, 'Os quadros continuam disponíveis nas áreas de trabalho.', { confirmLabel: 'Excluir', danger: true })) {
      deleteFavoriteFolder(folder.id);
    }
  };

  const boards = folder.boards || [];

  return (
    <div
      ref={setNodeRef}
      className={`rounded transition-colors ${isOver ? 'bg-accent/10 ring-1 ring-accent/40' : ''}`}
    >
      <div className="group/f flex items-center gap-1 px-4 py-1 text-xs text-text-secondary">
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-0.5 hover:text-text-primary"
        >
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <Star className="w-3 h-3 text-warning shrink-0" />
        {renaming ? (
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveName();
              if (e.key === 'Escape') { setName(folder.name); setRenaming(false); }
            }}
            className="flex-1 bg-transparent border border-accent/50 rounded px-1 text-xs text-text-primary outline-none"
          />
        ) : (
          <span className="flex-1 truncate font-medium">{folder.name}</span>
        )}
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="p-0.5 rounded opacity-0 group-hover/f:opacity-100 hover:bg-white/10 hover:text-text-primary transition-all"
          title="Opções"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
        {menuOpen && (
          <div
            ref={menuRef}
            className="absolute z-50 mt-24 ml-16 w-44 bg-card border border-border rounded-lg shadow-xl py-1"
          >
            <button
              onClick={() => { setMenuOpen(false); setRenaming(true); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5"
            >
              <Pencil className="w-3.5 h-3.5" /> Renomear
            </button>
            <button
              onClick={handleDelete}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger hover:bg-danger/10"
            >
              <Trash2 className="w-3.5 h-3.5" /> Excluir pasta
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div>
          {boards.length === 0 ? (
            <div className="pl-10 pr-4 py-1 text-[11px] text-text-secondary/60 italic">
              Arraste um board aqui
            </div>
          ) : (
            boards.map((b) => (
              <DraggableBoardItem
                key={`${folder.id}-${b.id}`}
                board={b}
                active={b.id === activeBoardId}
                onClick={() => onNavigate(`/board/${b.id}`)}
                sourceFolderId={folder.id}
                indent={10}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeBoardFromFolder(folder.id, b.id);
                  }}
                  className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10 text-text-secondary hover:text-danger transition-all"
                  title="Remover da pasta"
                >
                  <X className="w-3 h-3" />
                </button>
              </DraggableBoardItem>
            ))
          )}
        </div>
      )}
    </div>
  );
}
